import { addDays, differenceInDays, format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import Spinner from '@/components/Spinner/Spinner'
import { useTheme } from '@/components/ThemeProvider/ThemeProvider'
import { RankOverTimeType, SetsType } from '@/types/page'

import styles from './RankOverTimePanel.module.scss'

type RankOverTimePanelProps = {
  rankOverTime: RankOverTimeType
}

const formatDate = (value: number) => {
  const date = addDays(new Date(0), value)
  return format(date, 'MMM yy')
}

const getThemeColors = (key: string): Record<'axis' | 'historyline' | 'setline' | 'grid' | 'bg' | 'text', string> => {
  if (key === 'dark') {
    return {
      axis: '#c8c8c8',
      bg: '#22262a',
      grid: '#606060',
      historyline: '#1763b5',
      setline: '#303030',
      text: '#dfe3e7',
    }
  }
  return {
    axis: '#303030',
    bg: '#fff',
    grid: '#c0c0c0',
    historyline: '#1763b5',
    setline: '#f0f0f0',
    text: '#505050',
  }
}

const yAxisDomain = ([dataMin, dataMax]: [dataMin: number, dataMax: number]): [number, number] => [
  Math.max(dataMin - Math.floor((dataMax - dataMin) * 0.25), 1),
  dataMax + Math.floor((dataMax - dataMin) * 0.25),
]

const SetLabel = ({ set, svgInfo }: { set: SetsType; svgInfo?: { viewBox?: { x?: number; y?: number } } }) => {
  const [theme] = useTheme()

  const themeColors = useMemo(() => getThemeColors(theme), [theme])

  const x = svgInfo?.viewBox?.x || false
  const y = svgInfo?.viewBox?.y || false

  return (
    x !== false &&
    y !== false && (
      <g>
        <rect fill={themeColors.bg} height={24} width={32} x={x - 16} y={y - 28} />
        <text fill={themeColors.text} font-size={12} text-anchor='middle' width={36} x={x} y={y - 10}>
          {set.code.toUpperCase()}
        </text>
      </g>
    )
  )
}

const TimelinePanel = ({ rankOverTime }: RankOverTimePanelProps) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [setsRecent, setSetsRecent] = useState<SetsType[]>([])
  const [theme] = useTheme()

  const themeColors = useMemo(() => getThemeColors(theme), [theme])

  useEffect(() => {
    ;(async () => {
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_EDHREC_JSON_URL}/static/sets-recent`)
        if (!res.ok) {
          throw new Error()
        }
        setSetsRecent((await res.json()) as SetsType[])
      } catch (e) {
        // Fails gracefully since set data is not integral to the chart
        console.error('Failed to retrieve sets-recent JSON', e)
      }
      setIsLoaded(true)
    })()
  }, [])

  const data = useMemo(
    () =>
      Object.entries(rankOverTime).map(([key, value]) => ({
        date: differenceInDays(new Date(key), new Date(0)),
        rank: value?.rank,
      })),
    [rankOverTime],
  )

  return (
    <div className={styles.rankOverTime}>
      <h5 className='d-flex justify-content-center w-100'>Popularity History</h5>
      <p className='d-flex justify-content-center w-100'>Rank of new decks made with this commander each month</p>
      {!isLoaded ? (
        <Spinner className='fa-2x' />
      ) : (
        <>
          <ResponsiveContainer height={300} width='100%'>
            <LineChart accessibilityLayer data={data} margin={{ bottom: 12, left: -20, right: 28, top: 32 }}>
              <XAxis
                angle={-25}
                dataKey='date'
                domain={['dataMin', 'dataMax']}
                interval={1}
                scale='linear'
                stroke={themeColors.axis}
                tick={{ fontSize: 12 }}
                tickFormatter={formatDate}
                tickMargin={8}
                type='number'
              />
              <YAxis
                domain={yAxisDomain}
                reversed
                scale='linear'
                stroke={themeColors.axis}
                tick={{ fontSize: 12 }}
                tickCount={4}
              />
              <CartesianGrid stroke={themeColors.grid} strokeDasharray='4 2' strokeMiterlimit={2} />
              <Tooltip
                contentStyle={{ background: themeColors.bg }}
                formatter={(value) => [value, 'Monthly Rank']}
                labelFormatter={formatDate}
              />
              {setsRecent.reverse().map((set) => (
                <ReferenceLine
                  key={set.code}
                  label={(svgInfo) => <SetLabel set={set} svgInfo={svgInfo} />}
                  stroke={themeColors.setline}
                  strokeWidth={1}
                  x={differenceInDays(new Date(set.released_at), new Date(0))}
                />
              ))}
              <Line
                activeDot={{ r: 6 }}
                dataKey='rank'
                stroke={themeColors.historyline}
                strokeWidth={3}
                type='monotone'
              />
              {data.length >= 18 && (
                <Brush
                  data={data}
                  dataKey='rank'
                  fill={themeColors.setline}
                  height={32}
                  startIndex={12}
                  stroke={themeColors.historyline}
                  tickFormatter={() => ''}
                  travellerWidth={8}
                  y={268}
                >
                  <LineChart>
                    <YAxis domain={yAxisDomain} hide reversed />
                    <Line dataKey='rank' dot={false} stroke={themeColors.historyline} strokeWidth={2} type='monotone' />
                  </LineChart>
                </Brush>
              )}
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  )
}

export default TimelinePanel
